import { useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';
import { md5 } from '@/lib/md5';

// ============================================================
// Mot de passe oublié.
// On vérifie l'existence du compte, on crée un jeton de
// réinitialisation (1 heure) puis on envoie un email via l'Edge
// Function `send-password-reset`. Réponse toujours générique pour
// ne pas révéler l'existence d'un compte.
// ============================================================

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [note, setNote] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    setNote('');
    try {
      const cleanEmail = email.trim();
      const { data: users } = await supabase
        .from('users')
        .select('id, email, name')
        .eq('email', cleanEmail)
        .limit(1);

      const target = users && users.length > 0 ? users[0] : null;

      if (target) {
        // Jeton opaque : partie aléatoire + empreinte (jamais réversible).
        const raw = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;
        const token = md5(raw) + Math.random().toString(36).slice(2, 10);
        const expiresAt = new Date(Date.now() + 60 * 60 * 1000).toISOString();

        await supabase.from('password_resets').insert({
          user_id: target.id,
          email: target.email,
          token,
          expires_at: expiresAt,
          used: false,
        });

        const basePath = __BASE_PATH__.split('/').filter(Boolean).join('/');
        const pathPrefix = basePath ? `/${basePath}` : '';
        const resetUrl = `${window.location.origin}${pathPrefix}/reinitialiser-mot-de-passe?token=${encodeURIComponent(token)}`;

        const { data: fnData } = await supabase.functions.invoke('send-password-reset', {
          body: { email: target.email, resetUrl, name: target.name || '' },
        });
        if (fnData && (fnData as { ok?: boolean }).ok === false) {
          setNote("L'envoi d'emails n'est pas encore configuré sur la plateforme. Contactez le support Zifek pour réinitialiser votre mot de passe.");
        }
      }

      setSent(true);
    } catch {
      setNote('Une erreur est survenue. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background-50 flex flex-col">
      <Navbar forceScrolled={true} />
      <main className="flex-1 flex items-center justify-center px-4 pt-20 pb-10">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-4">
              <i className="ri-lock-password-line text-2xl text-primary-600"></i>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
              Mot de passe oublié
            </h1>
            <p className="text-foreground-500 text-sm">
              Saisissez votre email : nous vous enverrons un lien de réinitialisation.
            </p>
          </div>

          <div className="bg-background-50 border border-background-200/70 rounded-lg p-6 md:p-8">
            {sent ? (
              <div className="text-center space-y-4">
                <div className="w-12 h-12 rounded-full bg-accent-100 flex items-center justify-center mx-auto">
                  <i className="ri-mail-check-line text-2xl text-accent-600"></i>
                </div>
                <p className="text-sm text-foreground-700">
                  Si un compte est associé à <strong>{email}</strong>, un lien de réinitialisation vient d&apos;être envoyé. Pensez à vérifier vos spams.
                </p>
                {note && (
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-md px-3 py-2 flex items-start gap-2 text-left">
                    <i className="ri-alert-line mt-0.5 flex-shrink-0"></i>{note}
                  </p>
                )}
                <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors">
                  <i className="ri-arrow-left-line"></i>Retour à la connexion
                </Link>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {note && (
                  <div className="p-3 rounded-md bg-red-50/80 border border-red-100 text-red-700 text-sm flex items-start gap-2">
                    <i className="ri-error-warning-line flex-shrink-0 mt-0.5"></i>
                    <span>{note}</span>
                  </div>
                )}
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    Adresse email
                  </label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="vous@exemple.com"
                    required
                    className="w-full h-11 px-4 rounded-md border border-background-200/70 bg-background-50 text-sm text-foreground-950 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 transition-colors"
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full h-11 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-foreground-800 transition-colors cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {loading ? (<><i className="ri-loader-4-line animate-spin"></i>Envoi...</>) : 'Envoyer le lien'}
                </button>
                <div className="pt-2 text-center">
                  <Link to="/login" className="text-sm text-foreground-500 hover:text-foreground-800 transition-colors">
                    Je me souviens de mon mot de passe
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}